import {
  BusinessEntityType,
  useGetBusinessEntitiesQuery,
} from "../../../../../graphql/generated";
import { ConditionValueSelectorComponentProps } from "../../ConditionForm";
import BusinessEntityInput from "./BusinessEntityInput";
import { AutocompleteProps } from "@mui/material";
import useDebounce from "../../../../../utils/hooks/useDebounce";
import { useMemo, useState } from "react";

type BusinessEntityInputContainerProps =
  ConditionValueSelectorComponentProps & {
    businessEntityType: BusinessEntityType;
  } & Pick<AutocompleteProps<string, true, undefined, undefined>, "sx">;

const BusinessEntityInputContainer = ({
  businessEntityType,
  ...props
}: BusinessEntityInputContainerProps) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 250);

  const selectedValues = useMemo(() => {
    return Array.isArray(props.value)
      ? props.value
      : props.value
      ? [props.value]
      : [];
  }, [props.value]);

  // Query for the dropdown list (all entities or filtered by search)
  const listQuery = useGetBusinessEntitiesQuery(
    {
      businessEntityTypes: businessEntityType,
      includeInactive: true,
      filter: debouncedSearch
        ? {
            $or: [
              { name: { $regex: debouncedSearch, $options: "i" } },
              { code: { $regex: debouncedSearch, $options: "i" } },
            ],
          }
        : undefined,
    },
    {
      keepPreviousData: true,
    }
  );

  // Separate query for selected entities to ensure they always display correctly
  const selectedQuery = useGetBusinessEntitiesQuery(
    {
      businessEntityTypes: businessEntityType,
      includeInactive: true,
      filter: { _id: { $in: selectedValues } },
    },
    {
      enabled: selectedValues.length > 0,
      keepPreviousData: true,
    }
  );

  // Merge results: selected entities + list entities (deduplicated)
  const businessEntities = useMemo(() => {
    const listEntities = listQuery.data?.businessEntities.data ?? [];
    const selectedEntities = selectedQuery.data?.businessEntities.data ?? [];

    // Use a Map to deduplicate by _id
    const entityMap = new Map(listEntities.map((e) => [e._id, e]));
    selectedEntities.forEach((e) => entityMap.set(e._id, e));

    return Array.from(entityMap.values());
  }, [
    listQuery.data?.businessEntities.data,
    selectedQuery.data?.businessEntities.data,
  ]);
  return (
    <BusinessEntityInput
      {...props}
      businessEntities={businessEntities || []}
      onSearchChange={setSearch}
    />
  );
};

export default BusinessEntityInputContainer;
